import { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { CalendarClock, Save, RotateCcw, ChevronDown, ChevronRight, TriangleAlert, Crown } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

// Mirrors backend _build(): a folder's delay gates the NEXT folder, so the last one is inert.
const spanOf = (folders, delays) => {
  if (!folders || folders.length < 2) return 0;
  return folders.slice(0, -1).reduce((s, f) => s + (delays[f.id] ?? f.days_delay ?? 0), 0);
};

export default function AdminPacingTab() {
  const [data, setData] = useState(null);
  const [delays, setDelays] = useState({});
  const [modDelays, setModDelays] = useState({});
  const [launch, setLaunch] = useState('2026-11-11');
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (launchDate) => {
    try {
      const r = await axios.get(`${API}/admin/pacing`, { params: { launch: launchDate }, ...auth() });
      setData(r.data);
      setDelays({});
      setModDelays({});
    } catch (e) {
      toast.error('Could not load the pacing schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(launch); }, [load, launch]);

  const setDelay = (folderId, value) => {
    const n = Math.max(0, Math.min(365, parseInt(value, 10) || 0));
    setDelays((prev) => ({ ...prev, [folderId]: n }));
  };

  const setModDelay = (moduleId, value) => {
    const n = Math.max(0, Math.min(365, parseInt(value, 10) || 0));
    setModDelays((prev) => ({ ...prev, [moduleId]: n }));
  };

  const applyToModule = (mod, value) => {
    const next = { ...delays };
    mod.folders.forEach((f, i) => {
      if (i < mod.folders.length - 1) next[f.id] = value;
    });
    setDelays(next);
  };

  const scaleAll = (factor) => {
    if (!data) return;
    const next = { ...delays };
    data.modules.forEach((m) => m.folders.forEach((f, i) => {
      if (i < m.folders.length - 1) {
        const cur = next[f.id] ?? f.days_delay ?? 0;
        next[f.id] = Math.max(0, Math.round(cur * factor));
      }
    }));
    setDelays(next);
  };

  // Live projection from the unsaved edits
  const projection = useMemo(() => {
    if (!data) return null;
    let cum = 0;
    const rows = data.modules.map((m) => {
      const breather = m.order > 1 ? (modDelays[m.id] ?? m.module_delay ?? 0) : 0;
      const span = breather + Math.max(spanOf(m.folders, delays), m.content_days || 0);
      cum += span;
      const d = new Date(`${launch}T12:00:00`);
      d.setDate(d.getDate() + cum);
      return { ...m, liveBreather: breather, liveSpan: span, liveCum: cum, liveDate: d };
    });
    return { rows, total: cum, finish: rows.length ? rows[rows.length - 1].liveDate : null };
  }, [data, delays, modDelays, launch]);

  const allFolders = useMemo(
    () => (data ? data.modules.flatMap((m) => m.folders) : []), [data]);

  const dirtyFolders = Object.entries(delays).filter(([id, v]) => {
    const f = allFolders.find((x) => x.id === id);
    return f && (f.days_delay ?? 0) !== v;
  });
  const dirtyModules = Object.entries(modDelays).filter(([id, v]) => {
    const m = data?.modules.find((x) => x.id === id);
    return m && (m.module_delay ?? 0) !== v;
  });
  const dirtyCount = dirtyFolders.length + dirtyModules.length;

  const save = async () => {
    if (!dirtyCount) return toast.info('Nothing changed yet');
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/pacing`, {
        updates: dirtyFolders.map(([folder_id, days_delay]) => ({ folder_id, days_delay })),
        module_updates: dirtyModules.map(([module_id, days_delay]) => ({ module_id, days_delay })),
      }, { params: { launch }, ...auth() });
      setData(r.data);
      setDelays({});
      setModDelays({});
      toast.success(`Saved — ${r.data.changed} delay${r.data.changed === 1 ? '' : 's'} retuned. Program is now ${r.data.total_days} days.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-gray-400">Loading the release schedule…</div>;
  if (!data || !projection) return null;

  const totalDelta = projection.total - data.total_days;

  return (
    <div className="space-y-5" data-testid="admin-pacing-tab">
      {/* Summary bar */}
      <Card className="bg-slate-900/70 border-white/10">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-end gap-x-10 gap-y-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mb-1.5">Launch date</div>
              <Input
                type="date"
                value={launch}
                onChange={(e) => setLaunch(e.target.value)}
                className="w-44 bg-slate-950/60 border-white/15 text-white"
                data-testid="pacing-launch-date"
              />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mb-1.5">Program length</div>
              <div className="text-3xl font-semibold text-white leading-none" data-testid="pacing-total-days">
                {projection.total}<span className="text-base font-normal text-gray-400 ml-1.5">days</span>
                {totalDelta !== 0 && (
                  <span className={`ml-3 text-base font-medium ${totalDelta > 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {totalDelta > 0 ? '+' : ''}{totalDelta}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                {(projection.total / 7).toFixed(1)} weeks · {(projection.total / 30.44).toFixed(1)} months
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mb-1.5">Module 13 complete</div>
              <div className="text-xl text-white" data-testid="pacing-finish-date">
                {projection.finish?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' })}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" onClick={() => scaleAll(2)} className="border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="pacing-scale-2x">Double all</Button>
              <Button variant="outline" onClick={() => scaleAll(1.5)} className="border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="pacing-scale-15x">×1.5</Button>
              <Button variant="outline" disabled={!dirtyCount} onClick={() => { setDelays({}); setModDelays({}); }} className="border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="pacing-reset">
                <RotateCcw className="w-4 h-4 mr-1.5" />Revert
              </Button>
              <Button onClick={save} disabled={saving || !dirtyCount} data-testid="pacing-save">
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? 'Saving…' : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : 'Save'}
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-400 mt-5 max-w-4xl">
            Two levers. A <strong className="text-gray-200">module breather</strong> holds the whole module
            shut for N days after the member finishes the one before it. A folder's delay is the wait before
            the <strong className="text-gray-200">next folder in that module</strong> opens — so the last
            folder in a module is inert, and a module with few folders finishes fast no matter what.
            All waits are measured from when the member actually completes the step, so these are minimums.
          </p>
        </CardContent>
      </Card>

      {/* Module rows */}
      {projection.rows.map((m) => {
        const isOpen = open[m.id];
        const delta = m.liveSpan - m.span_days;
        return (
          <Card key={m.id} className="bg-slate-900/50 border-white/10 overflow-hidden" data-testid={`pacing-module-${m.order}`}>
            <button
              onClick={() => setOpen((p) => ({ ...p, [m.id]: !p[m.id] }))}
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/5 transition-colors"
              data-testid={`pacing-module-toggle-${m.order}`}
            >
              {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
              <span className="text-sm font-mono text-gray-500 w-6 shrink-0">{m.order}</span>
              <span className="text-white font-medium truncate flex-1 min-w-0">{m.title}</span>

              {m.tier_ceiling?.length > 0 && (
                <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 shrink-0 gap-1">
                  <Crown className="w-3 h-3" />{m.tier_ceiling.join(' / ')} ends here
                </Badge>
              )}
              {m.folders.length <= 4 && (
                <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 shrink-0">
                  only {m.folders.length} folders
                </Badge>
              )}
              {m.liveBreather > 0 && (
                <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30 shrink-0">
                  +{m.liveBreather}d breather
                </Badge>
              )}
              <span className="text-xs text-gray-500 shrink-0 w-24 text-right">{m.content_count} items</span>
              <span className="text-sm text-white shrink-0 w-20 text-right" data-testid={`pacing-span-${m.order}`}>
                {m.liveSpan}d
                {delta !== 0 && <span className={delta > 0 ? 'text-emerald-400 ml-1' : 'text-amber-400 ml-1'}>{delta > 0 ? '+' : ''}{delta}</span>}
              </span>
              <span className="text-sm text-gray-400 shrink-0 w-28 text-right">day {m.liveCum}</span>
              <span className="text-sm text-gray-300 shrink-0 w-32 text-right">
                {m.liveDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: '2-digit' })}
              </span>
            </button>

            {isOpen && (
              <div className="border-t border-white/10 bg-slate-950/40 px-5 py-4">
                {m.order > 1 && (
                  <div className="flex items-center gap-4 pb-4 mb-4 border-b border-white/10" data-testid={`pacing-breather-row-${m.order}`}>
                    <span className="text-xs uppercase tracking-widest text-gray-500">Module breather</span>
                    <span className="text-sm text-gray-300 flex-1 min-w-0">
                      Hold this whole module shut for this many days after they finish Module {m.order - 1}
                    </span>
                    <Input
                      type="number" min="0" max="365"
                      value={modDelays[m.id] ?? m.module_delay ?? 0}
                      onChange={(e) => setModDelay(m.id, e.target.value)}
                      className={`w-20 h-8 text-right bg-slate-950/60 border-white/15 text-white shrink-0 ${(m.module_delay ?? 0) !== (modDelays[m.id] ?? m.module_delay ?? 0) ? 'ring-1 ring-emerald-400/60' : ''}`}
                      data-testid={`pacing-module-delay-${m.order}`}
                    />
                    <span className="text-xs text-gray-500 w-8 shrink-0">days</span>
                  </div>
                )}

                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-gray-500 mr-1">Set every folder here to</span>
                  {[0, 3, 4, 7, 10, 14].map((v) => (
                    <Button
                      key={v} size="sm" variant="outline"
                      onClick={() => applyToModule(m, v)}
                      className="h-7 px-2.5 border-white/15 bg-transparent text-gray-200 hover:bg-white/10"
                      data-testid={`pacing-apply-${m.order}-${v}`}
                    >{v}d</Button>
                  ))}
                  {m.wasted_days > 0 && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-amber-300">
                      <TriangleAlert className="w-3.5 h-3.5" />
                      last folder has a {m.wasted_days}-day delay that does nothing
                    </span>
                  )}
                </div>

                <div className="space-y-1.5">
                  {m.folders.map((f, i) => {
                    const val = delays[f.id] ?? f.days_delay ?? 0;
                    const edited = (f.days_delay ?? 0) !== val;
                    return (
                      <div key={f.id} className="flex items-center gap-4 py-1.5" data-testid={`pacing-folder-row-${f.id}`}>
                        <span className="text-xs font-mono text-gray-600 w-6 shrink-0">{i + 1}</span>
                        <span className="text-sm text-gray-200 flex-1 min-w-0 truncate">{f.name}</span>
                        {f.is_trailing ? (
                          <span className="text-xs text-gray-500 shrink-0">last folder — delay has no effect</span>
                        ) : (
                          <span className="text-xs text-gray-500 shrink-0">wait before folder {i + 2}</span>
                        )}
                        <Input
                          type="number" min="0" max="365" value={val}
                          onChange={(e) => setDelay(f.id, e.target.value)}
                          disabled={f.is_trailing}
                          className={`w-20 h-8 text-right bg-slate-950/60 border-white/15 text-white shrink-0 ${edited ? 'ring-1 ring-emerald-400/60' : ''} ${f.is_trailing ? 'opacity-40' : ''}`}
                          data-testid={`pacing-input-${f.id}`}
                        />
                        <span className="text-xs text-gray-500 w-8 shrink-0">days</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        );
      })}

      <p className="text-sm text-gray-500 pb-4">
        Measured against the live gates by <code className="text-gray-400">backend/scripts/drip_speed_test.py</code>.
        Saved changes take effect immediately for every member. Launch reference: {fmt(launch)}.
      </p>
    </div>
  );
}
